import { ReactElement, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button, Menu, MenuProps, Typography } from 'antd';
import Flexbox from '@common/components/Flexbox';
import { EAppRoutes, MainMenuKeyType, Permission } from '@common/enums';
import { useAuthContextProvider } from '@common/hooks/auth/AuthContextProvider';
import {
  DataAudienceOutlinedIcon,
  DealManagerOutlinedIcon,
  LeftArrowIcon,
  LogoutOutlinedIcon,
  RightArrowIcon,
} from '@common/resources/icons';
import { getObjectKeyByValue } from '@common/utils/object-utils';
import * as Styled from './Sider.styled';

const { Title } = Typography;

export type MenuItem = Required<MenuProps>['items'][number];

const Sider = (): ReactElement => {
  const authContext = useAuthContextProvider();

  const location = useLocation();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [mainMenuSelectedItems, setMainMenuSelectedItems] = useState<string[]>(
    location.pathname !== EAppRoutes.Home && Object.values(EAppRoutes).includes(location.pathname as EAppRoutes)
      ? MainMenuKeyType[getObjectKeyByValue(EAppRoutes, location.pathname)!]
      : []
  );

  useEffect(() => {
    if (location.pathname !== EAppRoutes.Home && Object.values(EAppRoutes).includes(location.pathname as EAppRoutes)) {
      setMainMenuSelectedItems(MainMenuKeyType[getObjectKeyByValue(EAppRoutes, location.pathname)]);
    }
  }, [location.pathname]);

  const handleCollapseButtonClick = (): void => {
    setIsCollapsed(() => !isCollapsed);
  };

  const handleMenuClick = (info: { key: string }): void => {
    setMainMenuSelectedItems([info.key]);
  };

  const mainMenuItems: MenuItem[] = [
    authContext.checkAuthAndPermissions(Permission.AccessDealModule) && {
      key: 'inventory',
      icon: <DealManagerOutlinedIcon />,
      label: (
        <Link
          data-test-id={'inventory-link'}
          to={{
            pathname: EAppRoutes.Inventory,
            search: `${location.search}`,
          }}>
          Deal Manager
        </Link>
      ),
      onClick: handleMenuClick,
    },
    authContext.checkAuthAndPermissions(Permission.AccessAudienceModule) && {
      key: 'dataAudience',
      icon: <DataAudienceOutlinedIcon />,
      label: (
        <Link
          to={{
            pathname: EAppRoutes.DataAudience,
            search: `${location.search}`,
          }}>
          Data & Audience
        </Link>
      ),
      onClick: handleMenuClick,
    },
    { type: 'divider', style: { margin: '0 8px' } },
    {
      key: 'logout',
      icon: <LogoutOutlinedIcon />,
      label: (
        <Link
          data-test-id={'logout-link'}
          to={{
            pathname: EAppRoutes.Logout,
            search: `${location.search}`,
          }}>
          Logout
        </Link>
      ),
      onClick: handleMenuClick,
    },
  ];

  return (
    <Styled.StyledSider width={'264px'} collapsible={true} collapsed={isCollapsed} trigger={null} collapsedWidth={72}>
      <Flexbox flexDirection='column' width={'100%'}>
        <Flexbox
          width={'100%'}
          alignItems='center'
          justifyContent='space-between'
          style={{ height: 88, padding: '0 20px', marginBottom: 24 }}>
          {!isCollapsed && (
            <Title style={{ margin: '0', fontWeight: 600 }} level={4}>
              Menu
            </Title>
          )}
          <Button
            data-test-id='sidebar-collapse-button'
            htmlType='button'
            type='primary'
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0' }}
            onClick={handleCollapseButtonClick}
            shape='round'>
            {isCollapsed ? (
              <RightArrowIcon style={{ height: '11.5px', width: '20px' }} />
            ) : (
              <LeftArrowIcon style={{ height: '11.5px', width: '20px' }} />
            )}
          </Button>
        </Flexbox>
        <Menu mode='vertical' items={mainMenuItems} selectedKeys={mainMenuSelectedItems} />
      </Flexbox>
    </Styled.StyledSider>
  );
};

export default Sider;
